import type { ComponentType, ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { QueryBoundary } from '@siliconx/shared/ui/QueryBoundary';
import { EmptyState } from '@siliconx/shared/ui/state';
import { ExportButtonGroup } from './ExportButtons';
import { ImportFromErpLink } from './ImportFromErpLink';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';

/**
 * SIL-319 (Arch 5) — shared skeleton for the customer financials/report
 * screens (book-vs-fmv, depreciation, opportunity, market-trends,
 * data-health). Builds on SIL-321's `QueryBoundary` for the top-level
 * loading/error/data state machine and folds in the export-header +
 * "empty → deep-link to ERP import" pattern that was previously copied and
 * drifted per screen (erp-register-import-spec.md §1 decision 7 — "every
 * Financials 'not enough data' empty state deep-links into the wizard").
 *
 * **NO capability gate here** (product decision, 2026-07-28, SIL-319):
 * financials reports describe the org's own assets and are not seller-
 * capability functions — `canSell`/`ProfileLockedCard` gating was removed
 * from the three screens that had it. Server auth (`clientAuth` + RLS) is
 * the access control; see CONTEXT.md "capability flags (canSell/canBuy)".
 *
 * **Non-goals (deliberately out of scope):** this module owns ONLY the
 * top-level query passed in via `query`. Nested drill-down queries (e.g.
 * book-vs-fmv's per-class NBV variance, depreciation's per-class asset
 * schedule + its own isFetching "Load more"), mutations + their
 * invalidation (data-health's lifecycle/ack mutations), and client-side
 * filter state (market-trends' class/SKU filters) all stay self-managed in
 * `children` / the route component — never hoisted here.
 */
export interface FinancialsReportEmptyConfig {
  icon: ComponentType<{ className?: string }>;
  title: string;
  body?: string;
}

export interface FinancialsReportScreenProps<TData> {
  title: string;
  subtitle: string;
  /** SIL-364: this screen's own nav route — renders a Pin/Pinned toggle in
   * the header. */
  pinTo: string;
  query: UseQueryResult<TData>;
  /** CSV export wiring for the header's `ExportButtonGroup`. Omit for
   * screens with no export (none currently, but not every report need
   * carry one). PDF export is folded in as `ExportButtonGroup`'s own
   * "coming soon" button — never a per-screen concern. */
  exportCsv?: { path: string; filename: string };
  /**
   * Resolves the successful query's data to a full-page empty-state
   * config, or `null` when there's data worth rendering. Omit entirely
   * when the screen never wants a full-page empty state (data-health:
   * summary + possibly-empty anomaly cards always render). The resolved
   * empty state always deep-links to ERP import — the one deliberate
   * exception is a screen that legitimately has nothing to do with ERP
   * import data (none of the current five omit it; add an `action` escape
   * hatch here if a future screen needs to).
   */
  empty?: (data: TData) => FinancialsReportEmptyConfig | null;
  /** Overrides the default `Couldn't load ${title.toLowerCase()}.` error title. */
  errorTitle?: string;
  /** Epic 3 (lots-first-class) — an optional control rendered beside the
   * export buttons, e.g. the "Group by" dimension selector on Financial
   * Position / Opportunity Cost. Always renders (unlike the export buttons,
   * which gate on a successful non-empty query) — switching dimension is
   * how a screen GETS out of an empty state for a dimension whose data
   * hasn't arrived yet (e.g. no lots assigned). */
  headerExtra?: ReactNode;
  children: (data: TData) => ReactNode;
}

export function FinancialsReportScreen<TData>({
  title,
  subtitle,
  pinTo,
  query,
  exportCsv,
  empty,
  errorTitle,
  headerExtra,
  children,
}: FinancialsReportScreenProps<TData>) {
  const resolvedEmpty = query.isSuccess ? (empty?.(query.data) ?? null) : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <PageHeader title={title} subtitle={subtitle} pinTo={pinTo} />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {headerExtra}
          {/* Export buttons render from the resolved empty boolean — never
           * per-screen `canSell && data` logic. */}
          {exportCsv && query.isSuccess && !resolvedEmpty && (
            <ExportButtonGroup csvPath={exportCsv.path} csvFilename={exportCsv.filename} />
          )}
        </div>
      </div>

      <QueryBoundary query={query} errorTitle={errorTitle ?? `Couldn't load ${title.toLowerCase()}.`}>
        {(data) => {
          const emptyConfig = empty?.(data) ?? null;
          if (emptyConfig) {
            return (
              <EmptyState
                icon={emptyConfig.icon}
                title={emptyConfig.title}
                description={emptyConfig.body}
                action={<ImportFromErpLink />}
              />
            );
          }
          return <>{children(data)}</>;
        }}
      </QueryBoundary>
    </div>
  );
}
